import { ObservableInputsManager } from '@/shared/utils/angular-sdk';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { UiSelectComponent } from '@/ui/select/ui-select';
import { UiToggleComponent } from '@/ui/toggle/ui-toggle';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, type OnChanges, type OnInit, Output, type SimpleChanges, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, type UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import type { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import type { BusinessIntelligenceDimensionConfiguration } from '../../../../interfaces/TailoredWidget.interface';
import { BusinessIntelligenceProvider } from '../../../../providers/business-intelligence/business-intelligence.provider';
import { BusinessIntelligenceRepresentationConfigEditorComponent } from '../business-intelligence-representation-config-editor.class';
import type { BusinessIntelligenceDimension, BusinessIntelligenceMeasure } from './../../../../interfaces/BusinessIntelligence.interface';
import type { BusinessIntelligencePieChartRepresentationConfig } from './../../../../interfaces/BusinessIntelligenceRepresentation.interface';

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-pie-chart-representation-config-editor.scss'],
    selector: 'business-intelligence-pie-chart-representation-config-editor',
    templateUrl: 'business-intelligence-pie-chart-representation-config-editor.html',
    imports: [FormsModule, ReactiveFormsModule, UiFormFieldComponent, UiSelectComponent, UiToggleComponent, AsyncPipe, TranslatePipe]
})
export class BusinessIntelligencePieChartRepresentationConfigEditorComponent
    extends BusinessIntelligenceRepresentationConfigEditorComponent<BusinessIntelligencePieChartRepresentationConfig>
    implements OnInit, OnChanges
{
    private _businessIntelligenceProvider = inject(BusinessIntelligenceProvider);
    private _fb = inject(UntypedFormBuilder);

    private _observableInputsManager = new ObservableInputsManager<BusinessIntelligencePieChartRepresentationConfigEditorComponent>(this);

    @Input() config!: BusinessIntelligencePieChartRepresentationConfig;
    config$ = this._observableInputsManager.observe('config');

    @Input() availableMeasures: BusinessIntelligenceMeasure[] = [];
    availableMeasures$ = this._observableInputsManager.observe('availableMeasures');

    @Input() availableDimensions: BusinessIntelligenceDimension[] = [];
    availableDimensions$ = this._observableInputsManager.observe('availableDimensions');

    @Input() disabled!: boolean;

    @Output() configChanged: EventEmitter<BusinessIntelligencePieChartRepresentationConfig> = new EventEmitter();

    formGroup!: UntypedFormGroup;
    segmentationDimensions$!: Observable<BusinessIntelligenceDimension[]>;

    ngOnInit() {
        this.segmentationDimensions$ = this.availableDimensions$.pipe(
            filter(isNotNullish),
            map(dimensions => dimensions.filter(dimension => !this._businessIntelligenceProvider.isContinuousDimension(dimension)))
        );
        this.formGroup = this._fb.group({
            measureCode: [null, Validators.required],
            segmentationDimensionCode: [null],
            areDataLabelsActivated: [null]
        });

        this.config$.pipe(untilDestroyed(this)).subscribe(config => {
            if (!config) {
                this.formGroup.setValue(
                    {
                        measureCode: null,
                        segmentationDimensionCode: null,
                        areDataLabelsActivated: false
                    },
                    { emitEvent: false }
                );
            } else {
                this.formGroup.setValue(
                    {
                        measureCode: config.measure ? config.measure.measureCode : null,
                        segmentationDimensionCode: config.segmentation ? config.segmentation.dimensionCode : null,
                        areDataLabelsActivated: config.areDataLabelsActivated ? config.areDataLabelsActivated : false
                    },
                    { emitEvent: false }
                );
            }
        });

        this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe(formValue => {
            const segmentationDimension: BusinessIntelligenceDimensionConfiguration = formValue.segmentationDimensionCode
                ? {
                      dimensionCode: formValue.segmentationDimensionCode
                  }
                : null!;
            this.configChanged.emit({
                segmentation: segmentationDimension,
                measure: {
                    measureCode: formValue.measureCode
                },
                areDataLabelsActivated: formValue.areDataLabelsActivated
            });
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        this._observableInputsManager.triggerChanges(changes);
    }
}
